import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { WebSocketService } from '@qtek/core/websockets-core';
import { filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { SchemaEditorActions } from './schemas.actions';
import { selectProductSchemaTypes } from './schemas.reducer';
import { SchemasService } from './schemas.service';

@Injectable()
export class SchemasEditorEffects {
  private actions$ = inject(Actions);
  private schemasService = inject(SchemasService);
  private webSocketService = inject(WebSocketService);
  private store = inject(Store);

  connectDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchemaEditorActions.connectSchemas),
      switchMap(({ mysid }: { mysid: string }) =>
        this.schemasService
          .subscribe({ mysid })
          .pipe(
            takeUntil(
              this.actions$.pipe(ofType(SchemaEditorActions.disconnectSchemas))
            )
          )
      ),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return res
              ? SchemaEditorActions.getSchemasSuccess({ schemas: res })
              : SchemaEditorActions.getSchemasFailure();

          case 'upd':
            return res
              ? SchemaEditorActions.updateSchemaSuccess({ schema: res })
              : SchemaEditorActions.updateSchemaFailure({ error: sts });
          default:
            return {
              type: '[Schema] Unknown operation',
            };
        }
      })
    )
  );

  updateSchema$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SchemaEditorActions.updateSchema),
        tap(({ payload }) => {
          this.schemasService.updateAttribute(payload);
        })
      ),
    { dispatch: false }
  );

  private readonly pendingSids = new Set<string>();

  getSchema$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SchemaEditorActions.getSchema),
        withLatestFrom(this.store.select(selectProductSchemaTypes)),
        filter(
          ([{ sid }, schemas]) => !schemas[sid] && !this.pendingSids.has(sid)
        ),
        tap(([{ sid }]) => {
          this.pendingSids.add(sid);
          this.schemasService.getSchema(sid);
        })
      ),
    { dispatch: false }
  );

  getSchemaListener$ = createEffect(() =>
    this.webSocketService.getWebSocketIsAlive$().pipe(
      filter(Boolean),
      switchMap(() => this.webSocketService.getWebSocketSubject()),
      switchMap(socket =>
        socket
          .asObservable()
          .pipe(
            filter(message => message.op === 'get' && message.ent === 'sma')
          )
      ),
      map(({ op, res, sts, id }: any) => {
        if (res) {
          this.pendingSids.delete(res.sid);
          return SchemaEditorActions.getSchemaSuccess({ schema: res });
        }
        this.pendingSids.delete(id);
        return SchemaEditorActions.getSchemaFailure({ error: sts });
      })
    )
  );
}
